import { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  LayoutDashboard, FileText, Rocket, TrendingUp, Award,
  PenTool, Users, GraduationCap, Calendar, Handshake
} from "lucide-react";
import { OverviewTab } from "./OverviewTab";
import { ContentTab } from "./ContentTab";
import { NetworkingTab } from "./NetworkingTab";
import { RoleTab } from "./RoleTab";
import { ProgramsTab } from "./ProgramsTab";
import { EventsTab } from "./EventsTab";
import type { User as UserType, Article, Event } from "@shared/schema";

interface DashboardTabsProps {
  user: UserType;
  dashboardStats: {
    connections: number;
    pendingConnections: number;
    unreadMessages: number;
    unreadNotifications: number;
    creditBalance: number;
    monthlyCredits: number;
    approvedRoles: string[];
    pendingIntros: number;
    totalIntros: number;
  } | undefined;
  articles: Article[];
  events: Event[];
  leaders: UserType[];
  connections: any[];
  statsLoading: boolean;
  articlesLoading: boolean;
  eventsLoading: boolean;
  leadersLoading: boolean;
  connectionsLoading: boolean;
}

interface TabDef {
  id: string;
  label: string;
  icon: any;
}

const STORAGE_KEY = "cit-dashboard-tab";

export function DashboardTabs(props: DashboardTabsProps) {
  const { user, dashboardStats } = props;
  const approvedRoles = dashboardStats?.approvedRoles || [];

  const tabs: TabDef[] = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
  ];

  if (approvedRoles.includes("founder")) {
    tabs.push({ id: "founder", label: "Founder", icon: Rocket });
  }
  if (approvedRoles.includes("investor")) {
    tabs.push({ id: "investor", label: "Investor", icon: TrendingUp });
  }
  if (approvedRoles.includes("executive")) {
    tabs.push({ id: "executive", label: "Executive", icon: Award });
  }
  if (approvedRoles.includes("contributor")) {
    tabs.push({ id: "contributor", label: "Contributor", icon: PenTool });
  }
  if (approvedRoles.includes("advisor")) {
    tabs.push({ id: "advisor", label: "Advisor", icon: Handshake });
  }

  tabs.push({ id: "content", label: "Content", icon: FileText });
  tabs.push({ id: "networking", label: "Networking", icon: Users });
  tabs.push({ id: "programs", label: "Programs", icon: GraduationCap });
  tabs.push({ id: "events", label: "Events", icon: Calendar });

  const getInitialTab = () => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const urlTab = params.get("tab");
      if (urlTab && tabs.some(t => t.id === urlTab)) return urlTab;

      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && tabs.some(t => t.id === stored)) return stored;
      } catch {}

      const primaryRole = user.primaryRole || user.role;
      if (primaryRole && tabs.some(t => t.id === primaryRole)) return primaryRole;
    }
    return "overview";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {}
    const url = new URL(window.location.href);
    if (value === "overview") {
      url.searchParams.delete("tab");
    } else {
      url.searchParams.set("tab", value);
    }
    window.history.replaceState({}, "", url.toString());
  };

  useEffect(() => {
    if (!tabs.some(t => t.id === activeTab)) {
      handleTabChange("overview");
    }
  }, [approvedRoles.join(",")]);

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
      <div className="mb-6 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none">
        <TabsList className="inline-flex h-auto p-1 w-auto min-w-full sm:min-w-0 gap-1" data-testid="dashboard-tabs-list">
          {tabs.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className="gap-1.5 px-3 py-2 text-xs sm:text-sm"
              data-testid={`tab-trigger-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      <TabsContent value="overview" data-testid="tab-content-overview">
        <OverviewTab {...props} />
      </TabsContent>

      {approvedRoles.includes("founder") && (
        <TabsContent value="founder" data-testid="tab-content-founder">
          <RoleTab role="founder" user={user} dashboardStats={dashboardStats} />
        </TabsContent>
      )}
      {approvedRoles.includes("investor") && (
        <TabsContent value="investor" data-testid="tab-content-investor">
          <RoleTab role="investor" user={user} dashboardStats={dashboardStats} />
        </TabsContent>
      )}
      {approvedRoles.includes("executive") && (
        <TabsContent value="executive" data-testid="tab-content-executive">
          <RoleTab role="executive" user={user} dashboardStats={dashboardStats} />
        </TabsContent>
      )}
      {approvedRoles.includes("contributor") && (
        <TabsContent value="contributor" data-testid="tab-content-contributor">
          <RoleTab role="contributor" user={user} dashboardStats={dashboardStats} />
        </TabsContent>
      )}
      {approvedRoles.includes("advisor") && (
        <TabsContent value="advisor" data-testid="tab-content-advisor">
          <RoleTab role="advisor" user={user} dashboardStats={dashboardStats} />
        </TabsContent>
      )}

      <TabsContent value="content" data-testid="tab-content-content">
        <ContentTab
          user={user}
          articles={props.articles}
          articlesLoading={props.articlesLoading}
        />
      </TabsContent>

      <TabsContent value="networking" data-testid="tab-content-networking">
        <NetworkingTab
          user={user}
          dashboardStats={dashboardStats}
          connections={props.connections}
          connectionsLoading={props.connectionsLoading}
        />
      </TabsContent>

      <TabsContent value="programs" data-testid="tab-content-programs">
        <ProgramsTab user={user} />
      </TabsContent>

      <TabsContent value="events" data-testid="tab-content-events">
        <EventsTab
          events={props.events}
          leaders={props.leaders}
          eventsLoading={props.eventsLoading}
          leadersLoading={props.leadersLoading}
        />
      </TabsContent>
    </Tabs>
  );
}
